// Generated by hand — see web/scripts/lint-dbtypes.sh for drift check.
// One interface per CREATE TABLE in web/db/schema.sql. The singular naming convention
// is enforced by the drift checker (snake_case → PascalCase, trailing 's' stripped).
// All TEXT columns are `string`; nullable columns get `| null`. INTEGER → number.
// REAL → number. Boolean-like INTEGERs (0/1) are typed as number for SQL fidelity.
//
// API DTOs at the bottom of this file are NOT tables — they are response shapes for
// the 8 endpoints in web/routes/api.js (added in Plan 05-02).

// Phase 08 — Map page imports (MAP-01).
// Re-exported at the bottom of the file alongside the map DTOs.
import type { AmenityCategory as _AmenityCategory } from '@/lib/map-constants';
import type { NearestCategory as _NearestCategory } from '@/lib/nearest-constants';

// ============================================================
// Tables — keep in sync with web/db/schema.sql
// ============================================================

export interface Property {
  id: number;
  address: string;
  sanitized_address: string;
  lat: number | null;
  lon: number | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  county: string | null; // Phase 05 ALTER (D6)
  created_at: string;
  updated_at: string;
}

export interface ListingDetail {
  id: number;
  property_id: number;
  zillow_url: string | null;
  price: number | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  year_built: number | null;
  updated_at: string;
}
export type ListingDetails = ListingDetail; // schema name (plural)

export interface Job {
  id: number;
  property_id: number;
  job_name: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed' | string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  job_type: string | null;
  refresh_diff: string | null;
}

export interface AgentRun {
  id: number;
  job_id: number;
  agent_name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | string;
  status_message: string | null;
  current_step: number | null;
  total_steps: number | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
}

export interface FloodData {
  id: number;
  property_id: number;
  fema_zone: string | null;
  flood_panel: string | null;
  risk_level: string | null;
  insurance_required: number | null;
  updated_at: string;
}

export interface InternetProvider {
  id: number;
  property_id: number;
  provider_name: string;
  connection_type: string | null;
  max_speed_mbps: number | null;
  starting_price: string | null;
  fiber_available: number | null;
}

export interface WalkabilityVenue {
  id: number;
  property_id: number;
  category: string;
  name: string;
  address: string | null;
  distance_mi: number | null;
  walk_min: number | null;
  cuisine: string | null;
}

export interface DogVenue {
  id: number;
  property_id: number;
  category: string;
  name: string;
  address: string | null;
  lat: number | null;
  lon: number | null;
  rating: string | null;
  description: string | null;
  distance_mi: number | null;
  walk_min: number | null;
}

export interface PropertyImage {
  id: number;
  property_id: number;
  filename: string;
  original_url: string | null;
  source: string | null;
  sort_order: number | null;
  is_favorite: number;
}

export interface SummaryRating {
  id: number;
  property_id: number;
  dog_friendliness: string | null;
  walkability: string | null;
  flood_risk: string | null;
  internet: string | null;
  entertainment: string | null;
  updated_at: string;
}
export type SummaryRatings = SummaryRating; // schema name (plural)

export interface EntertainmentVenue {
  id: number;
  property_id: number;
  category: string;
  name: string;
  address: string | null;
  lat: number | null;
  lon: number | null;
  rating: string | null;
  distance_mi: number | null;
  walk_min: number | null;
  description: string | null;
}

export interface EntertainmentScore {
  id: number;
  property_id: number;
  overall_score: number | null;
  nightlife_score: number | null;
  live_music_score: number | null;
  sports_score: number | null;
  cultural_score: number | null;
  karaoke_score: number | null;
}
export type EntertainmentScores = EntertainmentScore;

export interface DogAmenity {
  id: number;
  name: string;
  category: 'dog_park' | 'daycare' | 'beach' | 'bar';
  lat: number | null;
  lon: number | null;
  address: string | null;
  city: string | null;
  county: string | null;
  source_url: string | null;
  last_verified_date: string | null;
  seasonal_restriction: string | null;
  access_hours: string | null;
  designation_type: string | null;
  coord_verified: number;
  confidence: string | null;
  created_at: string;
  updated_at: string;
}

export interface DogAmenityStaging {
  id: number;
  name: string;
  category: 'dog_park' | 'daycare' | 'beach' | 'bar';
  lat: number | null;
  lon: number | null;
  address: string | null;
  city: string | null;
  county: string | null;
  source_url: string | null;
  last_verified_date: string | null;
  seasonal_restriction: string | null;
  access_hours: string | null;
  designation_type: string | null;
  coord_verified: number;
  confidence: string | null;
  source_type: string | null;
  created_at: string;
}
export type DogAmenitiesStaging = DogAmenityStaging; // schema name (plural form)

export interface DedupReview {
  id: number;
  record_a_id: number;
  record_b_id: number;
  category: string;
  distance_m: number | null;
  name_ratio: number | null;
  status: string;
  created_at: string;
}

export interface CountyCoverage {
  id: number;
  county: string;
  category: string;
  record_count: number;
  collection_status: string | null;
  gap_reason: string | null;
  last_checked_at: string;
}

// Phase 05 Plan 01 ranking engine
export interface PropertyRanking {
  property_id: number;
  recency_score: number | null;
  walkability_score: number | null;
  entertainment_score: number | null;
  weights_version: number;
  computed_at: string;
}

export interface RankingWeight {
  weights_version: number;
  w_recency: number;
  w_walkability: number;
  w_entertainment: number;
  w_dog_proximity: number;
  dog_signal_mode: 'gate' | string;
  recency_model: 'tiered' | string;
  updated_at: string;
}
export type RankingWeights = RankingWeight; // schema name (plural)

// ============================================================
// API DTOs (NOT tables) — response shapes for /api/* endpoints
// ============================================================

export interface AmenityCounts {
  dog_park: number;
  beach: number;
  daycare: number;
  bar: number;
}

export interface RankedPropertyDTO {
  property_id: number;
  address: string;
  /**
   * Filesystem-safe address slug. Mirrors `properties.sanitized_address`.
   * Used by the dashboard to resolve hero photo URLs at
   * `/reports/<sanitized_address>/images/zillow/zillow-1.jpg`.
   */
  sanitized_address: string;
  county: string | null;
  /** Latitude/longitude included for future map-side wiring; unused on dashboard. */
  lat: number | null;
  lon: number | null;
  created_at: string;
  composite: number;
  /** Renormalized static sub-score 0..100 (D4 cold-start). */
  static_score: number;
  recency_score: number | null;
  walkability_score: number | null;
  entertainment_score: number | null;
  dog_proximity_factor: number;
  amenities_in_radius_by_category: AmenityCounts;
  is_incomplete: boolean;
  weights_version: number;
  /**
   * Phase 06 CARRY-2 — pre-resolved hero photo URL from the API.
   * Server resolves `LEFT JOIN property_images ORDER BY is_favorite DESC, id ASC LIMIT 1`
   * and exposes the resulting URL here. `null` when the property has no photos.
   * Phase 05 callers may continue to omit this field; Phase 06 callers prefer it
   * over the legacy path-guessing fallback.
   */
  hero_image_url?: string | null;
  /** Latest listing price (USD). `null` when no listing_details row exists. Drives `sort: 'price'`. */
  price?: number | null;
  /**
   * Distance (miles) to the nearest amenity of the requested `nearestCat`.
   * Only populated by the server when `sort: 'nearest'`; `null` otherwise or
   * when no such amenity exists (e.g. a lifestyle category beyond the
   * property's walkability scan). Sorts to the bottom (nulls-last).
   */
  nearest_distance_mi?: number | null;
}

export interface StatsTopFit {
  id: number;
  address: string;
  composite: number;
}

export interface StatsFreshest {
  id: number;
  address: string;
  created_at: string;
}

export interface StatsDTO {
  total: number;
  avg_fit: number | null;
  top_fit: StatsTopFit | null;
  freshest: StatsFreshest | null;
}

export type SortKey = 'composite' | 'dog' | 'walk_ent' | 'recency' | 'price' | 'nearest';
export type RecencyFilter = 'all' | '7d' | '30d' | '90d';

export interface FilterState {
  radius: number;
  sort: SortKey;
  /**
   * Amenity type targeted by `sort: 'nearest'` (dashboard). Ignored for every
   * other sort. Serialized to the URL only when `sort === 'nearest'`.
   */
  nearestCat: _NearestCategory;
  county: string[];
  recency: RecencyFilter;
  cats: string[];
  /**
   * Phase 08 (Plan 08-02) — Map-only: whether properties inside any visible
   * amenity radius get the lit/larger styling. Default `true`. The dashboard
   * does not surface this control but `parseFilters` reads it from the URL
   * so navigation between `/app/` and `/app/map` is bidirectional.
   */
  highlight: boolean;
  /**
   * Phase 08-r (Plan 260502-08r) — Map-only: when `true`, property markers
   * whose center is OUTSIDE the radius circle of every visible amenity are
   * hidden from the map entirely (vs `highlight` which only restyles them).
   * Default `false` — first-time visitors see all properties; this is an
   * opt-in filter. Same URL round-trip discipline as `highlight`.
   */
  inRadiusOnly: boolean;
  /**
   * Phase 08-r — Map-only: whether to render the amenity radius rings on the
   * map. Default `false` — with 539 amenities and overlapping radii the rings
   * are visually noisy by default; users opt-in via a chip when they want to
   * see the geometry.
   */
  showRadiusCircles: boolean;
  /**
   * Phase 08-r — Map-only: whether to render the amenity marker pins. Default
   * `false` — the user wants the map to feel property-first; the in/out-of-
   * radius classification still runs (so HighlightToggle and RadiusOnlyToggle
   * work as expected), but the pins themselves only appear when this is on.
   */
  showAmenityPins: boolean;
}

// ============================================================
// Phase 06 — Property Detail DTOs (DETAIL-02 single round-trip)
// ============================================================

/**
 * Photo manifest entry returned by `GET /api/properties/:id`.
 * Resolved server-side from the `property_images` table; `url` is a
 * fully-qualified path under the existing `/reports/...` static mount.
 */
export interface PhotoDTO {
  id: number;
  url: string;
  is_favorite: boolean;
  alt: string;
  source: string | null;
}

/**
 * Row shape for the `photo_jobs` table — tracks per-property photo collection
 * runs (Street View / Zillow / Deep Dive). Excluded from import-existing.js
 * drop list (cross-run state, like dog_amenities).
 */
export interface PhotoJob {
  id: number;
  property_id: number;
  source: 'streetview' | 'zillow' | 'deep' | string;
  status: 'running' | 'completed' | 'failed' | string;
  message: string | null;
  credits_estimate: number | null;
  credits_used: number | null;
  photos_added: number | null;
  started_at: string;
  finished_at: string | null;
}
export type PhotoJobs = PhotoJob; // schema name (plural) for lint-dbtypes

/**
 * Agent report identifiers — keys of the `agent_reports` map on the
 * detail-page response. Order is irrelevant in the DTO; rendering order
 * is locked by `<AgentReportTabs>` (UI-SPEC § 3.5).
 */
export type AgentReportKey =
  | 'walkability'
  | 'entertainment'
  | 'dog_friendliness'
  | 'flood'
  | 'internet'
  | 'property_photos'
  | 'compiled';

/**
 * Single agent report entry. `html` is server-rendered markdown via
 * `marked` (existing v1.0 dep); `null` indicates the file does not exist
 * (status `pending` / `failed`). `report_path` is the relative path under
 * `/reports/<sanitized>/` for diagnostics; the client never fetches it
 * (the round-trip already includes the rendered HTML).
 */
export interface AgentReportEntry {
  status: 'completed' | 'failed' | 'pending';
  html: string | null;
  report_path: string;
  errorMessage?: string;
}

/**
 * Per-category neighbor counts inside the configured radius (default 0.5 mi
 * per Phase 06 CONTEXT.md). Mirrors the dog-amenity category vocabulary.
 */
export interface NeighborsInRadius {
  dog_park: number;
  dog_beach: number;
  daycare: number;
  dog_bar: number;
}

/**
 * Ranking sub-shape for the detail page. Matches the runtime payload
 * produced by `web/lib/property-detail-query.js` (Plan 06-01) — includes
 * the static_score + sub-scores + dog_proximity_factor for the radar
 * chart, plus the radius-tagged amenity counts inside the dog circle.
 */
export interface PropertyRankingDTO {
  composite: number;
  static_score: number;
  recency_score: number | null;
  walkability_score: number | null;
  entertainment_score: number | null;
  dog_proximity_factor: number;
  amenities_in_radius_by_category: AmenityCounts;
  is_incomplete: boolean;
  weights_version: number | null;
  radius_mi: number;
}

/**
 * Single round-trip response from `GET /api/properties/:id`. All related
 * data is denormalized into one envelope; the client never makes follow-up
 * queries on the detail route except the photo-favorite POST.
 *
 * Sentinel exclusion: `__amenity_refresh__` returns 404 (per CONTEXT.md).
 */
export interface PropertyDetailDTO {
  property: Property & { is_incomplete: boolean };
  ranking: PropertyRankingDTO;
  listing_details: ListingDetails | null;
  summary_ratings: SummaryRatings | null;
  flood: FloodData | null;
  internet: InternetProvider[];
  photos: PhotoDTO[];
  agent_reports: Record<AgentReportKey, AgentReportEntry>;
  neighbors_in_radius: NeighborsInRadius;
}

// ============================================================
// Phase 07 — Jobs UI DTOs
// ============================================================

/**
 * UI-canonical agent vocabulary. The server stores hyphenated v1.0 names
 * (e.g. 'dog-friendliness') — `/api/jobs` translates to/from these
 * snake_case keys at the API boundary (Plan 07-01). Order is locked to
 * UI-SPEC § 3.2 (the 7-dot grid execution order).
 */
export type AgentName =
  | 'walkability'
  | 'dog_friendliness'
  | 'flood_zone'
  | 'internet_providers'
  | 'entertainment'
  | 'property_images'
  | 'compiled';

export const AGENT_NAMES: readonly AgentName[] = [
  'walkability',
  'dog_friendliness',
  'flood_zone',
  'internet_providers',
  'entertainment',
  'property_images',
  'compiled',
] as const;

export type JobStatus = 'pending' | 'running' | 'completed' | 'failed';

/** GET /api/jobs row shape — list view. */
export interface JobListDTO {
  job_id: number;
  job_name: string | null;
  address: string;
  sanitized_address: string;
  property_id: number;
  status: JobStatus;
  job_type: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  duration_sec: number | null;
  /** Map keyed by AgentName; every key always present (missing rows → 'pending'). */
  agent_progress: Record<AgentName, JobStatus>;
}

/**
 * Per-agent run status from /api/jobs/:id/status. The legacy v1.0 endpoint
 * keeps hyphenated names (EJS clients still read it) — see § 3.3 of UI-SPEC
 * for how the React detail view consumes these.
 */
export interface AgentRunDTO {
  /** Hyphenated server form (e.g. 'dog-friendliness'). UI title-cases for display. */
  name: string;
  status: JobStatus;
  status_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  current_step: number;
  total_steps: number;
  percentage: number;
}

/** GET /api/jobs/:id/status — detail view payload. */
export interface JobDetailDTO {
  job_id: number;
  status: JobStatus;
  property_id: number;
  address: string;
  cli_running: boolean | null;
  overall_percentage: number;
  agents: AgentRunDTO[];
}

/** POST /api/jobs response. */
export interface SubmitJobResponse {
  job_id: number;
  property_id: number;
  job_name: string;
  address: string;
  sanitized_address: string;
  status: JobStatus;
}

// ============================================================
// Phase 08 — Map page DTOs (MAP-01)
// ============================================================

/**
 * GET /api/map-data response shape (existing v1.0 endpoint).
 * Sentinel `__amenity_refresh__` is already excluded server-side
 * (web/routes/api.js). All rows have non-null lat/lon — the SQL query
 * filters them out at the source. The client treats lat/lon as required
 * after the API boundary check but the type allows null for defensive
 * coding (a stray row would skip rendering, not crash).
 */
export interface MapAmenity {
  id: number;
  name: string;
  /** DB-canonical category name (matches AMENITY_CATEGORIES.db). */
  category: _AmenityCategory;
  lat: number | null;
  lon: number | null;
  address: string | null;
  source_url: string | null;
  last_verified_date: string | null;
  seasonal_restriction: string | null;
  designation_type: string | null;
  confidence: string | null;
  county: string | null;
}

export interface MapProperty {
  id: number;
  address: string;
  lat: number | null;
  lon: number | null;
}

/** Top-level GET /api/map-data envelope. */
export interface MapDataResponse {
  properties: MapProperty[];
  amenities: MapAmenity[];
}

/** Re-export from map-constants so callers have one type origin (db.ts). */
export type { AmenityCategory } from '@/lib/map-constants';
/** Re-export from nearest-constants so callers have one type origin (db.ts). */
export type { NearestCategory } from '@/lib/nearest-constants';

// ============================================================
// Phase 09 — Admin (ADMIN-01, ADMIN-02, ADMIN-03)
// ============================================================

export type AdminCollectionStatus =
  | 'collected'
  | 'partial'
  | 'failed'
  | 'bringfido_skipped'
  | null;

/** Single cell in the county × category matrix. */
export interface AdminMatrixCell {
  count: number;
  status: AdminCollectionStatus;
  last_checked_at: string | null;
  gap_reason: string | null;
}

export interface AdminAmenitiesDTO {
  counties: string[];
  categories: ['dog_park', 'beach', 'daycare', 'bar'];
  matrix: Record<string, Record<string, AdminMatrixCell>>;
  total_amenities: number;
}

export type AdminRefreshScope =
  | { scope: 'all' }
  | { scope: 'county'; county: string }
  | { scope: 'category'; category: string };

export interface AdminRefreshResponse {
  ok: true;
  job_id: number;
  job_name: string;
}

export interface AdminRecomputeAcceptedResponse {
  ok: true;
  weights_version: number;
  started_at: string;
}

export interface RecomputeStatusDTO {
  running: boolean;
  started_at: string | null;
  finished_at: string | null;
  last_run: string | null;
  processed: number;
  weights_version: number | null;
  error: string | null;
}

// ============================================================
// Phase 11 Plan 04 — Tiered photo collection DTOs
// ============================================================

/**
 * Response from POST /api/properties/:id/photos/collect.
 * `estimate` is only present for source='deep' (static credit heuristic).
 * `current_balance` may be present when the server reads the balance inline.
 */
export interface PhotoCollectResponse {
  photo_job_id: number;
  estimate?: number | null;
  current_balance?: number | null;
}

/**
 * Response from GET /api/properties/:id/photos/status.
 * When no photo_jobs row exists, only `status: 'idle'` is returned.
 * Otherwise all photo_jobs columns are present.
 */
export interface PhotoJobStatusDTO extends Partial<PhotoJob> {
  status: 'idle' | 'running' | 'completed' | 'failed' | string;
}

/**
 * Response from GET /api/credits.
 * `balance` is the remaining Firecrawl credits; `null` when the server
 * could not parse the output (graceful fallback, always HTTP 200).
 * `total` is the plan capacity; also null on parse failure.
 */
export interface CreditBalanceDTO {
  balance: number | null;
  total: number | null;
}
